<template>
  <div v-if="ongoing" class="progress-time" :class="{ compact }">
    <div class="line before" :style="{ width: beforeWidth }" />
    <span class="time"> {{ time }} </span>
    <div class="line" />
  </div>
</template>
<script lang="ts">
import { computed, ComputedRef, defineComponent, PropType, toRefs } from "vue";

import type { Event } from "@ctrl/controller-rpc";

import {
  useEventProgress,
  useHeightOfDuration,
} from "@/components/agenda/pixels";
import { getDaytime } from "@/components/dates";
import * as inject from "@/components/inject";
import { UseSettings } from "@/components/settings/controller/settings";

export default defineComponent({
  props: {
    event: { type: Object as PropType<Event>, required: true },
    compact: { type: Boolean, default: false },
  },
  setup(props) {
    const clock = inject.clock();
    const locale = inject.locale();
    const { event } = toRefs(props);
    const time = computed(() =>
      locale.value.formatDaytime(getDaytime(clock.value))
    );
    const { ongoing } = useHeightOfDuration(event, clock);

    // Progress
    const { progress } = useEventProgress(event, clock);
    const { is24hFormat } = UseSettings.getInstance();
    const multiple: ComputedRef<number> = computed(() =>
      is24hFormat.value ? 82 : 78
    );
    const beforeWidth: ComputedRef<string> = computed(() =>
      progress.value > 100
        ? "100%"
        : `${((progress.value + 3) * multiple.value) / 100}%`
    );

    return { beforeWidth, ongoing, time };
  },
});
</script>
<style lang="scss" scoped>
.progress-time {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: calc(100% - 8px);
  .time {
    @extend %font-caption-medium;
    padding: 2px 6px;
    border-radius: 30px;
    @include themed() {
      color: t($secondary);
      background-color: t($secondary-10);
    }
  }
  .line {
    height: 1px;
    width: 100%;
    @include themed() {
      background: t($grey-15);
    }
    &.before {
      flex-shrink: 0;
      @include themed() {
        background: linear-gradient(
          to right,
          t($grey-10) 0%,
          t($secondary) 20%
        );
      }
    }
  }
  &.compact {
    .time {
      padding: 0 6px;
      @extend %font-small-regular;
    }
    .line {
      background: transparent;
    }
  }
}
</style>
